import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { createCheckoutSession } from '../../../lib/api';
import { supabase } from '../../../lib/supabase';
import { Button } from '../button';
import { Card, CardContent } from '../card';

import { TokenUsageBar } from './components/token-usage-bar';
import { getPsTokensPurchaseContext } from './helpers/ps-tokens-logic';
import { formatExpiryDate, getTrialInfo } from './helpers/ps-tokens-display';
import type { PsTokensSectionProps } from './types';

const DEFAULT_DAILY_PS_TOKEN_LIMIT = 120;

export function PsTokensSection({
  authUser,
}: PsTokensSectionProps): JSX.Element {
  const { workspaceId, canPurchase } = getPsTokensPurchaseContext(authUser);
  const [searchParams, setSearchParams] = useSearchParams();

  const [buyLoading, setBuyLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<
    'success' | 'cancelled' | null
  >(null);
  const [dailyUsage, setDailyUsage] = useState(0);
  const [psBalance, setPsBalance] = useState(0);
  const [psTotal, setPsTotal] = useState(0);
  const [psExpiry, setPsExpiry] = useState<string | null>(null);
  const [dailyLimit, setDailyLimit] = useState(DEFAULT_DAILY_PS_TOKEN_LIMIT);

  const [userCreatedAt, setUserCreatedAt] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user?.created_at) setUserCreatedAt(data.user.created_at);
    });

    const checkout = searchParams.get('checkout');
    if (checkout === 'success' || checkout === 'cancelled') {
      setCheckoutStatus(checkout);
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
    }

    const resetLoading = () => setBuyLoading(false);
    window.addEventListener('pageshow', resetLoading);
    return () => window.removeEventListener('pageshow', resetLoading);
  }, [searchParams, setSearchParams]);

  const trial = userCreatedAt ? getTrialInfo(userCreatedAt) : null;
  const isTrialActive = trial?.isTrialActive ?? false;

  useEffect(() => {
    if (!workspaceId) return;

    supabase
      .from('workspaces')
      .select(
        'ps_token_balance, ps_token_total, ps_token_expiry, daily_ps_token_limit',
      )
      .eq('id', workspaceId)
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Failed to load workspace token data:', error);
          return;
        }
        if (data) {
          setPsBalance(data.ps_token_balance ?? 0);
          setPsTotal(data.ps_token_total ?? 0);
          setPsExpiry(data.ps_token_expiry ?? null);
          setDailyLimit(
            data.daily_ps_token_limit ?? DEFAULT_DAILY_PS_TOKEN_LIMIT,
          );
        }

        if (isTrialActive) {
          supabase
            .rpc('get_workspace_usage_today', { p_workspace_id: workspaceId })
            .then(({ data: usage, error: usageError }) => {
              if (usageError) {
                console.error('Failed to load daily usage:', usageError);
                return;
              }
              if (usage != null) setDailyUsage(Number(usage));
            });
        }
      });
  }, [workspaceId, isTrialActive]);

  const handleBuy = async () => {
    if (!workspaceId || !canPurchase) return;

    setBuyLoading(true);
    setCheckoutError(null);

    try {
      const { url } = await createCheckoutSession({ workspaceId });
      window.location.href = url;
    } catch (err) {
      setCheckoutError(
        err instanceof Error ? err.message : 'Failed to start checkout',
      );
      setBuyLoading(false);
    }
  };

  const hasTokens = psTotal > 0 && psBalance > 0;
  const expiryText = hasTokens ? formatExpiryDate(psExpiry) : null;
  const expirySubtitle =
    expiryText === 'Expired'
      ? 'Expired'
      : expiryText
        ? `Expires ${expiryText}`
        : null;

  return (
    <div className="space-y-4" data-onboarding="settings-tokens">
      <div>
        <h2 className="font-prompt-safe-com-semantic-heading-3 text-prompt-safecomvulcan">
          Token limits
        </h2>
        <p className="mt-1 text-sm text-prompt-safecomabbey">
          Purchased tokens are used when your workspace exceeds its daily free
          allowance.
        </p>
      </div>

      {checkoutStatus === 'success' && (
        <div className="rounded-lg border border-green-400 bg-green-50 px-4 py-3 text-sm text-green-700 shadow-sm">
          Payment successful! Your PS tokens have been added.
        </div>
      )}

      {checkoutStatus === 'cancelled' && (
        <div className="rounded-lg border border-yellow-400 bg-yellow-50 px-4 py-3 text-sm text-yellow-700 shadow-sm">
          Checkout was cancelled. No charges were made.
        </div>
      )}

      {checkoutError && (
        <div className="rounded-lg border border-red-400 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
          {checkoutError}
        </div>
      )}

      <Card>
        <CardContent className="space-y-5 pt-6">
          {isTrialActive && (
            <TokenUsageBar
              label="Free trial usage"
              used={dailyUsage}
              total={dailyLimit}
              subtitle={`${trial!.daysRemaining} day${trial!.daysRemaining === 1 ? '' : 's'} remaining`}
              limitMessage="Daily limit reached. Purchase PS tokens for additional usage."
            />
          )}

          <TokenUsageBar
            label="PS tokens"
            used={psTotal - psBalance}
            total={psTotal}
            subtitle={expirySubtitle}
            limitMessage="PS tokens exhausted. Purchase more to continue."
          />

          <div>
            <Button
              type="button"
              className="hover:bg-prompt-safecomoutrageous-orange/90 w-full bg-prompt-safecomoutrageous-orange text-prompt-safecomwhite sm:w-auto"
              disabled={!canPurchase || buyLoading}
              onClick={() => void handleBuy()}
            >
              {buyLoading ? 'Redirecting…' : 'Buy 2,000 PS tokens'}
            </Button>
            {!canPurchase && workspaceId && (
              <p className="mt-2 text-xs text-prompt-safecomabbey">
                Only the workspace owner (admin) can purchase PS tokens for this
                workspace.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
